import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchScope, isAbortError } from "../services/managerApi";

const STORAGE_KEY = "wosh.manager.collectionPoint";

const readStoredId = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const storeId = (id) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch (error) {
    /* stockage indisponible (navigation privée) — la sélection reste en mémoire */
  }
};

/**
 * Ramène la réponse de `/customer/manager/scope` à une liste utilisable.
 *
 * L'endpoint vient d'être livré et son enveloppe exacte n'a pas encore été
 * observée : on accepte les formes plausibles plutôt que de parier sur une
 * seule. Mais on ne se tait pas — une réponse non vide dont on ne sait rien
 * tirer est signalée, sinon l'écran affiche simplement « aucun établissement »
 * et personne ne sait que le contrat a divergé.
 */
export const normalizeScope = (scope) => {
  if (!scope) return [];

  const list = Array.isArray(scope)
    ? scope
    : scope.collection_points || scope.collectionPoints || scope.establishments || scope.companies;

  if (!Array.isArray(list) || !list.length) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[manager] périmètre reçu mais illisible :", scope);
    }
    return [];
  }

  return list
    .map((point) => ({
      ...point,
      collection_point_id: point.collection_point_id ?? point.collectionPointId ?? point.id ?? null,
      company_id: point.company_id ?? point.companyId ?? null,
      name: point.name || point.company_name || point.companyName || "",
    }))
    .filter((point) => point.name || point.collection_point_id != null);
};

/**
 * Résout les établissements dont le manager connecté est responsable.
 *
 * 7 managers sur 78 en gèrent plusieurs en production : la sélection est un cas
 * réel, pas une hypothèse. Elle est mémorisée entre les sessions pour éviter de
 * rebasculer sur le premier établissement à chaque connexion.
 *
 * Ce lien n'existe que côté serveur, dans `companies.manager_id`. Rien dans le
 * profil ne le porte :
 *
 *  - `users.company_id` est la société à laquelle le manager APPARTIENT comme
 *    client, pas celle qu'il gère. Sur staging, le manager 20026 porte
 *    `company_id = 348` tout en gérant la société 450.
 *  - `users.company_name` est une saisie libre d'inscription. Sur les 85
 *    sociétés gérées en production, 73 managers l'ont vide et 7 y ont écrit
 *    autre chose que le nom de la société qu'ils gèrent — elle ne tombe juste
 *    que dans 5 cas sur 85.
 *
 * Faute de source fiable, on ne renvoie donc AUCUN nom tant que
 * `/customer/manager/scope` n'est pas livré. Le repli précédent affichait le
 * prénom du manager, qui partait à l'impression sur les étiquettes.
 */
export default function useManagerScope(authUser) {
  const [collectionPoints, setCollectionPoints] = useState(null);
  const [selectedId, setSelectedId] = useState(readStoredId);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const scope = await fetchScope({ authUser, signal: controller.signal });
        if (!active) return;
        setCollectionPoints(normalizeScope(scope));
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
        setCollectionPoints([]);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser]);

  const selected = useMemo(() => {
    if (!collectionPoints?.length) return null;
    const match = collectionPoints.find(
      (point) => String(point.collection_point_id) === String(selectedId)
    );
    return match || collectionPoints[0];
  }, [collectionPoints, selectedId]);

  const select = useCallback((id) => {
    setSelectedId(id);
    storeId(id);
  }, []);

  return {
    // Vide tant que le serveur ne l'a pas donné — jamais deviné. Les écrans
    // qui l'impriment doivent pouvoir distinguer « inconnu » d'une valeur.
    establishmentName: selected?.name || "",
    collectionPoints: collectionPoints || [],
    selected,
    collectionPointId: selected?.collection_point_id ?? null,
    hasMultiple: (collectionPoints?.length || 0) > 1,
    isKnown: (collectionPoints?.length || 0) > 0,
    select,
    isLoading,
    error,
  };
}
